import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { PageHeader } from "@/components/layout/PageHeader";
import { StatusPill } from "@/components/shared/StatusPill";
import { Loading } from "@/components/shared/Loading";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useCustomerInvoices, useReadyToInvoice } from "@/api/hooks";
import { api } from "@/api/client";
import { apiErrorMessage } from "@/lib/apiError";
import { money } from "@/lib/format";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";
import { AlertCircle, Banknote, Check, FilePlus, Loader2, MessageCircle, WalletCards } from "lucide-react";
import { BusinessStage, stageForInvoice } from "@/components/shared/BusinessStage";

const today = () => new Date().toISOString().slice(0, 10);
const statusKey = (value) => String(value || "").trim().toUpperCase().replaceAll("_", " ");
const blockerMessage = (blocker) => typeof blocker === "string" ? blocker : blocker?.message || blocker?.code || "Invoice preparation is blocked.";
const blockersFor = (row) => Array.isArray(row?.blockers) ? row.blockers.map(blockerMessage) : row?.blocker ? [blockerMessage(row.blocker)] : [];
const outstandingFor = (invoice) => Number(
  invoice?.outstandingAmount ?? invoice?.outstanding ?? invoice?.balance ??
  Math.max(Number(invoice?.amount || 0) - Number(invoice?.paidAmount || 0), 0),
);

export default function FinanceReceivables() {
  const { openComposer } = useApp();
  const { data: invoices } = useCustomerInvoices();
  const { data: rti } = useReadyToInvoice();
  const qc = useQueryClient();
  const [draft, setDraft] = useState(null);
  const [draftingJobId, setDraftingJobId] = useState(null);
  const [approving, setApproving] = useState(false);
  const [paymentInvoice, setPaymentInvoice] = useState(null);
  const [payment, setPayment] = useState({ amount: "", reference: "", receivedAt: today() });
  const [paymentBusy, setPaymentBusy] = useState(false);

  if (!invoices || !rti) return <Loading />;

  const openDraft = async (jobId) => {
    setDraftingJobId(jobId);
    setDraft(null);
    try {
      const { data } = await api.post("/finance/final-invoice/draft", { jobId });
      setDraft({ ...data, total: Number(data.revenue ?? 0) });
    } catch (e) {
      toast.error(apiErrorMessage(e, "Final invoice draft could not be prepared."));
    } finally {
      setDraftingJobId(null);
    }
  };

  const approve = async () => {
    if (!draft) return;
    setApproving(true);
    try {
      await api.post("/finance/final-invoice/approve", {
        jobId: draft.jobId,
        total: Number(draft.total),
        lineItems: draft.lineItems,
      });
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["ar"] }),
        qc.invalidateQueries({ queryKey: ["rti"] }),
        qc.invalidateQueries({ queryKey: ["profit"] }),
      ]);
      toast.success("Final invoice approved and issued");
      setDraft(null);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Final invoice could not be issued."));
    } finally {
      setApproving(false);
    }
  };

  const openPayment = (invoice) => {
    setPaymentInvoice(invoice);
    setPayment({ amount: String(outstandingFor(invoice)), reference: "", receivedAt: today() });
  };

  const recordPayment = async (event) => {
    event.preventDefault();
    const amount = Number(payment.amount);
    if (!paymentInvoice || !Number.isFinite(amount) || amount <= 0 || !payment.reference.trim() || !payment.receivedAt) return;
    setPaymentBusy(true);
    try {
      await api.post(`/finance/customer-invoices/${paymentInvoice.id}/payment`, {
        amount,
        reference: payment.reference.trim(),
        receivedAt: payment.receivedAt,
        expectedVersion: Number(paymentInvoice.version ?? 0),
      });
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["ar"] }),
        qc.invalidateQueries({ queryKey: ["profit"] }),
        qc.invalidateQueries({ queryKey: ["customers"] }),
      ]);
      toast.success("Customer payment recorded");
      setPaymentInvoice(null);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Customer payment could not be recorded."));
    } finally {
      setPaymentBusy(false);
    }
  };

  const overdueInvoices = invoices.filter((invoice) => statusKey(invoice.status) === "OVERDUE");
  const openInvoices = invoices.filter((invoice) => ["OPEN", "OVERDUE", "PARTIALLY PAID"].includes(statusKey(invoice.status)));
  const overdueTotal = overdueInvoices.reduce((sum, invoice) => sum + outstandingFor(invoice), 0);

  return (
    <>
      <PageHeader title="Invoicing & AR" subtitle="Final invoicing, customer collections and payment reconciliation" />
      <div className="flex-1 overflow-y-auto p-6 space-y-5">
        <section>
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">Ready to invoice</div>
          <div className="space-y-3">
            {rti.length === 0 && <div className="rounded-lg border border-slate-200 bg-white p-6 text-sm text-slate-500">No jobs are currently ready for final invoicing.</div>}
            {rti.map((r) => {
              const blockers = blockersFor(r);
              const eligible = r.invoiceEligible === true;
              const drafting = draftingJobId === r.jobId;
              return (
                <div key={r.jobId} data-testid={`rti-${r.jobId}`} className="rounded-lg border border-slate-200 border-l-4 border-l-blue-800 bg-white p-4 flex items-start justify-between flex-wrap gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-display font-semibold text-slate-900">{r.customer} · Job {r.jobId}</div>
                      <StatusPill status={eligible ? "Ready" : "Blocked"} bucket={eligible ? "success" : "warning"} />
                    </div>
                    <div className="text-xs text-slate-600 mt-1">Revenue {money(r.revenue, r.currency || "AED")} · Cost {money(r.cost, r.currency || "AED")} · GP <span className="text-emerald-700 font-semibold">{money(r.gp, r.currency || "AED")} ({r.gpPct}%)</span></div>
                    {!eligible && (
                      <div className="mt-2 space-y-1" data-testid={`rti-blockers-${r.jobId}`}>
                        {(blockers.length ? blockers : ["Invoice eligibility is still being verified."]).map((message, index) => (
                          <div key={`${message}-${index}`} className="flex items-start gap-1.5 text-xs text-amber-800"><AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" /><span>{message}</span></div>
                        ))}
                      </div>
                    )}
                  </div>
                  <Button onClick={() => openDraft(r.jobId)} disabled={!eligible || draftingJobId !== null} data-testid="rti-prepare">
                    {drafting ? <Loader2 className="h-4 w-4 animate-spin" /> : <FilePlus className="h-4 w-4" />}Prepare final invoice
                  </Button>
                </div>
              );
            })}
          </div>
        </section>

        <section>
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">Accounts receivable</div>
          <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1080px] text-sm">
                <thead><tr className="border-b border-slate-200 bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500"><th className="text-left font-semibold px-4 py-2.5">Invoice</th><th className="text-left font-semibold px-4 py-2.5">Customer</th><th className="text-right font-semibold px-4 py-2.5">Outstanding</th><th className="text-left font-semibold px-4 py-2.5">Due</th><th className="text-left font-semibold px-4 py-2.5">Current stage / last status</th><th className="text-right font-semibold px-4 py-2.5">Actions</th></tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {invoices.map((inv) => {
                    const invoiceStatus = statusKey(inv.status);
                    const canRecordPayment = ["OPEN", "OVERDUE", "PARTIALLY PAID"].includes(invoiceStatus);
                    return (
                      <tr key={inv.id} data-testid={`ar-${inv.id}`} className="hover:bg-slate-50 transition-colors">
                        <td className="px-4 py-3 font-mono-num text-[13px] text-slate-700">{inv.id}<div className="text-[11px] text-slate-500">Job {inv.jobId}</div></td>
                        <td className="px-4 py-3 text-slate-700">{inv.customer}</td>
                        <td className="px-4 py-3 text-right font-mono-num font-semibold text-slate-900">{money(outstandingFor(inv), inv.currency || "AED")}</td>
                        <td className="px-4 py-3 text-xs text-slate-600">{inv.due}<div className={`text-[11px] ${invoiceStatus === "OVERDUE" ? "text-red-600" : "text-slate-500"}`}>{inv.aging}{inv.promise ? ` · ${inv.promise}` : ""}</div></td>
                        <td className="px-4 py-3"><BusinessStage stage={inv.businessStage || stageForInvoice(inv)} /></td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-2">
                            {invoiceStatus === "OVERDUE" && <Button size="sm" variant="outline" className="h-8" onClick={() => openComposer({ channel: "whatsapp", parties: ["Customer · Accounts"], defaultParty: "Customer · Accounts", customerId: inv.customerId, intent: "send a polite overdue payment reminder" })} data-testid={`ar-remind-${inv.id}`}><MessageCircle className="h-3.5 w-3.5" />Remind</Button>}
                            {canRecordPayment && <Button size="sm" className="h-8" onClick={() => openPayment(inv)} data-testid={`ar-payment-${inv.id}`}><Banknote className="h-3.5 w-3.5" />Record payment</Button>}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {invoices.length === 0 && <div className="p-8 text-center text-sm text-slate-500">No customer invoices have been issued.</div>}
          </div>
        </section>

        <div className="rounded-lg border border-slate-200 border-l-4 border-l-blue-800 bg-white p-4" data-testid="collections-summary">
          <div className="flex items-start gap-3">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-blue-50 text-blue-800"><WalletCards className="h-4 w-4" /></div>
            <div>
              <div className="text-sm font-semibold text-slate-900">Collections summary</div>
              <div className="mt-0.5 text-sm text-slate-600">
                {overdueInvoices.length > 0
                  ? `${overdueInvoices.length} overdue invoice${overdueInvoices.length === 1 ? "" : "s"} totaling ${money(overdueTotal)}. ${openInvoices.length} invoice${openInvoices.length === 1 ? " is" : "s are"} currently open for collection.`
                  : `No overdue invoices. ${openInvoices.length} invoice${openInvoices.length === 1 ? " is" : "s are"} currently open for collection.`}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={!!draft} onOpenChange={(open) => { if (!open && !approving) setDraft(null); }}>
        <DialogContent className="sm:max-w-lg" data-testid="final-invoice-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><FilePlus className="h-4 w-4 text-blue-800" />Final invoice · {draft?.customer}</DialogTitle>
            <DialogDescription>Review the approved commercial value and Operations actuals before issuing the invoice.</DialogDescription>
          </DialogHeader>
          {draft && (
            <div className="space-y-3">
              {draft.aiNote && <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700"><div className="mb-1 text-xs font-semibold text-slate-900">Draft review note</div>{draft.aiNote}</div>}
              <div className="grid grid-cols-3 gap-3 text-center">
                <Metric label="Quoted sell" value={money(draft.revenue, draft.currency || "AED")} />
                <Metric label="Actual cost" value={money(draft.actualCost, draft.currency || "AED")} muted />
                <Metric label="Margin" value={`${draft.marginPct}%`} accent />
              </div>
              <div>
                <label htmlFor="final-invoice-total" className="text-xs font-semibold text-slate-700">Invoice total ({draft.currency || "AED"})</label>
                <Input id="final-invoice-total" type="number" value={draft.total} readOnly aria-readonly="true" data-testid="final-invoice-total" className="mt-1 bg-slate-50 font-mono-num text-slate-700" />
                <div className="text-[11px] text-slate-500 mt-1">Locked to approved quotation {draft.quotationId || "—"}. Revise the quotation before invoicing if the commercial total is incorrect.</div>
              </div>
              <div className="flex items-center justify-end gap-2 pt-1">
                <Button variant="outline" onClick={() => setDraft(null)} disabled={approving} data-testid="final-invoice-cancel">Cancel</Button>
                <Button onClick={approve} disabled={approving} data-testid="final-invoice-approve">
                  {approving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}Approve and issue
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!paymentInvoice} onOpenChange={(open) => { if (!open && !paymentBusy) setPaymentInvoice(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="ar-payment-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><Banknote className="h-4 w-4 text-blue-800" />Record customer payment</DialogTitle>
            <DialogDescription>{paymentInvoice ? `${paymentInvoice.customer} · ${paymentInvoice.id}` : ""}</DialogDescription>
          </DialogHeader>
          <form className="space-y-4" onSubmit={recordPayment}>
            <div>
              <label htmlFor="ar-payment-amount" className="text-xs font-semibold text-slate-700">Amount received ({paymentInvoice?.currency || "AED"})</label>
              <Input id="ar-payment-amount" type="number" min="0.01" step="0.01" max={paymentInvoice ? outstandingFor(paymentInvoice) : undefined} value={payment.amount} onChange={(e) => setPayment((v) => ({ ...v, amount: e.target.value }))} className="mt-1 font-mono-num" required autoFocus />
            </div>
            <div>
              <label htmlFor="ar-payment-reference" className="text-xs font-semibold text-slate-700">Payment reference</label>
              <Input id="ar-payment-reference" value={payment.reference} onChange={(e) => setPayment((v) => ({ ...v, reference: e.target.value }))} placeholder="Bank transfer or receipt reference" className="mt-1" required />
            </div>
            <div>
              <label htmlFor="ar-payment-date" className="text-xs font-semibold text-slate-700">Received date</label>
              <Input id="ar-payment-date" type="date" value={payment.receivedAt} onChange={(e) => setPayment((v) => ({ ...v, receivedAt: e.target.value }))} className="mt-1" required />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" variant="outline" onClick={() => setPaymentInvoice(null)} disabled={paymentBusy}>Cancel</Button>
              <Button type="submit" disabled={paymentBusy || Number(payment.amount) <= 0 || !payment.reference.trim() || !payment.receivedAt} data-testid="ar-payment-submit">
                {paymentBusy && <Loader2 className="h-4 w-4 animate-spin" />}Record payment
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

const Metric = ({ label, value, accent, muted }) => <div className="rounded-md bg-slate-50 border border-slate-200 py-2"><div className={`font-display font-bold text-base font-mono-num ${accent ? "text-emerald-700" : muted ? "text-slate-500" : "text-slate-900"}`}>{value}</div><div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div></div>;
